<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import {
  PROP_TYPES,
  SUBTYPES,
  MARKETING_BADGES,
  CURRENCIES,
  FIELD_CLASS,
  SELECT_CLASS,
} from '../constants';
import { apiErrorMessage } from '../../../../api/realEstate/errors';
import ImageUpload from '../components/ImageUpload.vue';
import { reportInvalidForm } from '../formFeedback';

// Dual-purpose: creates a new project when no `project` prop is given,
// otherwise edits that project's Overview section only (status is edited
// from the standalone status dropdown on the detail page, not here). The
// cover image is a separate upload endpoint (docs/real-estate/be/
// 10-api-reference.md §A4): picking or deleting it only stages it, and Save
// sends it after the text fields — for a new project, right after it's
// created, since the uploads need its id. Same flow as the floor plan modal.
export default {
  name: 'OverviewModal',
  components: { ImageUpload },
  props: {
    project: {
      type: Object,
      default: null,
    },
  },
  data() {
    const p = this.project;
    return {
      propTypes: PROP_TYPES,
      marketingBadges: MARKETING_BADGES,
      currencies: CURRENCIES,
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      form: {
        name: p?.name || '',
        developer: p?.developer || '',
        project_code: p?.project_code || '',
        short_description: p?.short_description || '',
        long_description: p?.long_description || '',
        property_type: p?.property_type || PROP_TYPES[0],
        property_subtype: p?.property_subtype || SUBTYPES[PROP_TYPES[0]][0],
        marketing_badge: p?.marketing_badge || '',
        currency: p?.currency || CURRENCIES[0],
        website_url: p?.website_url || '',
      },
      isSubmitting: false,
      submitted: false,
      // null (unchanged), 'remove', or { file, previewUrl }.
      media: { cover: null },
    };
  },
  computed: {
    isEdit() {
      return !!this.project;
    },
    // `property_subtype` is free text on the API — SUBTYPES is only a
    // suggestion list for the datalist (docs/real-estate/be/07-fe-handoff.md
    // §4.1: "subtype — free text; prototype list = suggestions").
    subtypeOptions() {
      return SUBTYPES[this.form.property_type] || [];
    },
    fieldErrors() {
      return {
        name: !this.form.name.trim(),
        project_code: !this.form.project_code.trim(),
      };
    },
    isFormValid() {
      return !Object.values(this.fieldErrors).some(Boolean);
    },
    coverPreview() {
      const change = this.media.cover;
      if (change === 'remove') return '';
      if (change) return change.previewUrl;
      return this.savedValue('cover') || '';
    },
  },
  beforeDestroy() {
    this.releasePreview('cover');
  },
  methods: {
    ...mapActions('realEstate', [
      'createProject',
      'patchProject',
      'applyProjectCover',
    ]),
    savedValue(slot) {
      if (!this.project) return null;
      return slot === 'cover' ? this.project.cover_image_url : null;
    },
    releasePreview(slot) {
      const change = this.media[slot];
      if (change && change.previewUrl) URL.revokeObjectURL(change.previewUrl);
    },
    stageFile(slot, file) {
      this.releasePreview(slot);
      this.media[slot] = { file, previewUrl: URL.createObjectURL(file) };
    },
    // Delete on a staged file drops it; if a saved file sits behind it,
    // that one is marked for removal too, so the slot ends up empty.
    stageRemove(slot) {
      this.releasePreview(slot);
      this.media[slot] = this.savedValue(slot) ? 'remove' : null;
    },
    clearStaged(slot) {
      this.releasePreview(slot);
      this.media[slot] = null;
    },
    pendingNote(slot) {
      const change = this.media[slot];
      if (!change) return '';
      return change === 'remove'
        ? this.$t('REAL_ESTATE.PLANS.MEDIA_PENDING_REMOVE')
        : this.$t('REAL_ESTATE.PLANS.MEDIA_PENDING_UPLOAD');
    },
    // Returns false (after alerting) if an upload failed; whatever is still
    // staged stays staged, so Save again retries just that.
    async saveMedia(propertyId) {
      const { cover } = this.media;
      if (!cover) return true;
      try {
        await this.applyProjectCover({ propertyId, cover });
        this.clearStaged('cover');
        return true;
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.OVERVIEW.ERROR_COVER')
        );
        // eslint-disable-next-line no-console
        console.error(err);
        return false;
      }
    },
    async handleSubmit() {
      this.submitted = true;
      if (!this.isFormValid) {
        reportInvalidForm(this);
        return;
      }
      this.isSubmitting = true;
      try {
        if (this.isEdit) {
          await this.submitEdit();
        } else {
          await this.submitCreate();
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    async submitEdit() {
      try {
        await this.patchProject({
          propertyId: this.project.property_id,
          fields: { ...this.form, project_code: this.form.project_code.trim() },
        });
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.OVERVIEW.ERROR_UPDATE')
        );
        // eslint-disable-next-line no-console
        console.error(err);
        return;
      }
      if (!(await this.saveMedia(this.project.property_id))) return;
      useAlert(this.$t('REAL_ESTATE.OVERVIEW.SUCCESS_UPDATED'));
      this.$emit('close');
    },
    async submitCreate() {
      // marketing_badge isn't a POST /properties field — the real BE's
      // create schema is additionalProperties:false and only accepts
      // name/property_type/developer/short_description/long_description/
      // property_subtype/currency/website_url/project_code (docs/
      // real-estate/be/10-api-reference.md §A1); it's only settable via
      // PATCH once the project exists. Create first, then patch it in.
      const { marketing_badge: marketingBadge, ...createFields } = this.form;
      let created;
      try {
        created = await this.createProject({
          ...createFields,
          project_code: createFields.project_code.trim(),
        });
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.OVERVIEW.ERROR_CREATE')
        );
        // eslint-disable-next-line no-console
        console.error(err);
        return;
      }
      if (marketingBadge) {
        // Best-effort: the project exists either way at this point, so a
        // failure here shouldn't look like project creation itself failed.
        try {
          await this.patchProject({
            propertyId: created.property_id,
            fields: { marketing_badge: marketingBadge },
          });
        } catch (badgeErr) {
          // eslint-disable-next-line no-console
          console.error(badgeErr);
        }
      }
      // The project exists now whatever happens to its files: a failed
      // upload is alerted instead, and can be redone from Edit overview.
      if (await this.saveMedia(created.property_id)) {
        useAlert(this.$t('REAL_ESTATE.OVERVIEW.SUCCESS_CREATED'));
      }
      this.$router.push({
        name: 'real_estate_project',
        params: {
          accountId: this.$route.params.accountId,
          projectId: created.property_id,
        },
      });
      this.$emit('close');
    },
  },
};
</script>

<template>
  <div
    class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-xl max-h-[85vh] overflow-y-auto"
    >
      <div class="flex items-center mb-4 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">
          {{
            isEdit
              ? $t('REAL_ESTATE.OVERVIEW.MODAL_EDIT_TITLE')
              : $t('REAL_ESTATE.OVERVIEW.MODAL_CREATE_TITLE')
          }}
        </h1>
        <woot-button
          variant="clear"
          color-scheme="alert"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>

      <form class="space-y-4" novalidate @submit.prevent="handleSubmit">
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.OVERVIEW.FIELD_NAME') }}
            <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.name"
            type="text"
            data-testid="re-overview-name"
            :class="[
              fieldClass,
              {
                '!border-red-500': submitted && fieldErrors.name,
              },
            ]"
            :placeholder="$t('REAL_ESTATE.OVERVIEW.FIELD_NAME_PLACEHOLDER')"
            required
          />
          <p
            v-if="submitted && fieldErrors.name"
            data-testid="re-overview-name-error"
            class="mt-1 mb-0 text-xs text-red-500"
          >
            {{ $t('REAL_ESTATE.FORM.REQUIRED') }}
          </p>
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label
              class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
            >
              {{ $t('REAL_ESTATE.OVERVIEW.FIELD_DEVELOPER') }}
            </label>
            <input
              v-model="form.developer"
              type="text"
              :class="fieldClass"
              :placeholder="
                $t('REAL_ESTATE.OVERVIEW.FIELD_DEVELOPER_PLACEHOLDER')
              "
            />
          </div>
          <div>
            <label
              class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
            >
              {{ $t('REAL_ESTATE.OVERVIEW.FIELD_PROJECT_CODE') }}
              <span class="text-red-500">*</span>
            </label>
            <input
              v-model="form.project_code"
              type="text"
              data-testid="re-overview-project-code"
              :class="[
                fieldClass,
                {
                  '!border-red-500': submitted && fieldErrors.project_code,
                },
              ]"
              :placeholder="
                $t('REAL_ESTATE.OVERVIEW.FIELD_PROJECT_CODE_PLACEHOLDER')
              "
              required
            />
            <p
              v-if="submitted && fieldErrors.project_code"
              data-testid="re-overview-project-code-error"
              class="mt-1 mb-0 text-xs text-red-500"
            >
              {{ $t('REAL_ESTATE.FORM.REQUIRED') }}
            </p>
          </div>
        </div>
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.OVERVIEW.FIELD_TAGLINE') }}
          </label>
          <input
            v-model="form.short_description"
            type="text"
            :class="fieldClass"
            :placeholder="$t('REAL_ESTATE.OVERVIEW.FIELD_TAGLINE_PLACEHOLDER')"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.OVERVIEW.FIELD_LONG_DESC') }}
          </label>
          <textarea
            v-model="form.long_description"
            rows="4"
            :class="fieldClass"
            :placeholder="
              $t('REAL_ESTATE.OVERVIEW.FIELD_LONG_DESC_PLACEHOLDER')
            "
          />
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label
              class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
            >
              {{ $t('REAL_ESTATE.OVERVIEW.FIELD_TYPE') }}
            </label>
            <select v-model="form.property_type" :class="selectClass">
              <option v-for="type in propTypes" :key="type" :value="type">
                {{ type }}
              </option>
            </select>
          </div>
          <div>
            <label
              class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
            >
              {{ $t('REAL_ESTATE.OVERVIEW.FIELD_SUBTYPE') }}
            </label>
            <input
              v-model="form.property_subtype"
              type="text"
              list="realEstateSubtypeSuggestions"
              :class="fieldClass"
              :placeholder="
                $t('REAL_ESTATE.OVERVIEW.FIELD_SUBTYPE_PLACEHOLDER')
              "
            />
            <datalist id="realEstateSubtypeSuggestions">
              <option
                v-for="subtype in subtypeOptions"
                :key="subtype"
                :value="subtype"
              />
            </datalist>
          </div>
        </div>
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.OVERVIEW.FIELD_MARKETING_BADGE') }}
          </label>
          <input
            v-model="form.marketing_badge"
            type="text"
            list="realEstateMarketingBadgeSuggestions"
            :class="fieldClass"
            :placeholder="
              $t('REAL_ESTATE.OVERVIEW.FIELD_MARKETING_BADGE_PLACEHOLDER')
            "
          />
          <datalist id="realEstateMarketingBadgeSuggestions">
            <option
              v-for="badge in marketingBadges"
              :key="badge"
              :value="badge"
            />
          </datalist>
        </div>
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.OVERVIEW.FIELD_COVER_IMAGE') }}
          </label>
          <ImageUpload
            :preview-url="coverPreview"
            @select="stageFile('cover', $event)"
            @remove="stageRemove('cover')"
          />
          <p
            v-if="media.cover"
            class="mt-1 mb-0 text-xs text-amber-600 dark:text-amber-400"
          >
            {{ pendingNote('cover') }}
          </p>
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label
              class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
            >
              {{ $t('REAL_ESTATE.OVERVIEW.FIELD_CURRENCY') }}
            </label>
            <select v-model="form.currency" :class="selectClass">
              <option v-for="cur in currencies" :key="cur" :value="cur">
                {{ cur }}
              </option>
            </select>
          </div>
          <div>
            <label
              class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
            >
              {{ $t('REAL_ESTATE.OVERVIEW.FIELD_WEBSITE') }}
            </label>
            <input
              v-model="form.website_url"
              type="url"
              :class="fieldClass"
              :placeholder="
                $t('REAL_ESTATE.OVERVIEW.FIELD_WEBSITE_PLACEHOLDER')
              "
            />
          </div>
        </div>

        <div
          class="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-200 dark:border-slate-700"
        >
          <woot-button variant="clear" type="button" @click="$emit('close')">
            {{ $t('REAL_ESTATE.OVERVIEW.CANCEL') }}
          </woot-button>
          <woot-button type="submit" :is-loading="isSubmitting">
            {{
              isEdit
                ? $t('REAL_ESTATE.OVERVIEW.SAVE')
                : $t('REAL_ESTATE.OVERVIEW.CREATE')
            }}
          </woot-button>
        </div>
      </form>
    </div>
  </div>
</template>
